import { useCallback, useEffect, useRef, useState } from "react";
import jwt_decode from "jwt-decode";
import { getPadPending, getNotifications, markNotificationRead, markAllNotificationsRead } from "../../pages/digi/NewMasterSheet/TenantForm/Helpers/PadInfo/api";

export const POLL_MS = 90 * 1000;

/** Who the widget is for: roles that can act on PAD work. Managers/auditors need the mastersheet privilege. */
export function resolveViewer() {
  try {
    const token = window.localStorage.getItem("accessToken");
    if (!token || !window.localStorage.getItem("authUser")) return null;
    const d = jwt_decode(token);
    if (d?.exp && Date.now() > d.exp * 1000) return null;
    const role = d?.role || window.localStorage.getItem("role") || "";
    if (role === "company" || role === "admin") return { role, id: d?.id, canVerify: true };
    if (role === "manager" && d?.masterSheetPrivilege) return { role, id: d?.id, canVerify: false };
    if (role === "auditor" && d?.masterSheetPrivilege) return { role, id: d?.id, canVerify: true };
    return null;
  } catch (_) {
    return null;
  }
}

/**
 * Polls the pending-PAD feed and stored notifications while the tab is visible.
 * Refreshes on focus, on demand, and every POLL_MS.
 */
export function useNotificationFeed(enabled) {
  const [pending, setPending] = useState({ items: [], counts: { total: 0, danger: 0, warning: 0, info: 0 }, generatedAt: null, scope: null });
  const [notifications, setNotifications] = useState({ items: [], unread: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastLoaded, setLastLoaded] = useState(null);
  const inFlight = useRef(false);
  const mounted = useRef(true);

  const refresh = useCallback(async (fresh = false) => {
    if (!enabled || inFlight.current) return;
    inFlight.current = true;
    setLoading(true);
    try {
      const [p, n] = await Promise.all([getPadPending(fresh), getNotifications()]);
      if (!mounted.current) return;
      if (p?.data?.data) setPending(p.data.data);
      if (n?.data?.data) setNotifications(n.data.data);
      setError(null);
      setLastLoaded(new Date());
    } catch (e) {
      if (mounted.current) setError(e?.response?.data?.message || e?.message || "Could not load notifications");
    } finally {
      inFlight.current = false;
      if (mounted.current) setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    refresh(false);
    const id = setInterval(() => {
      if (document.visibilityState === "visible") refresh(false);
    }, POLL_MS);
    const onFocus = () => refresh(false);
    const onVisible = () => { if (document.visibilityState === "visible") refresh(false); };
    const onPadChanged = () => refresh(true);
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    // Fired by the PAD Info section after a write so the badge updates without waiting for the poll.
    window.addEventListener("rdc-pad-changed", onPadChanged);
    return () => {
      clearInterval(id);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("rdc-pad-changed", onPadChanged);
    };
  }, [enabled, refresh]);

  const markRead = useCallback(async (id) => {
    setNotifications((n) => ({
      items: n.items.map((i) => (i._id === id ? { ...i, read: true } : i)),
      unread: Math.max(0, n.unread - (n.items.find((i) => i._id === id && !i.read) ? 1 : 0)),
    }));
    try { await markNotificationRead(id); } catch (_) { /* optimistic; next poll reconciles */ }
  }, []);

  const markAllRead = useCallback(async () => {
    setNotifications((n) => ({ items: n.items.map((i) => ({ ...i, read: true })), unread: 0 }));
    try { await markAllNotificationsRead(); } catch (_) { /* optimistic */ }
  }, []);

  return { pending, notifications, loading, error, lastLoaded, refresh, markRead, markAllRead };
}
